import { useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import Image from 'next/image'
import Link from 'next/link'
import parse from 'html-react-parser'
import dayjs from 'utils/dayjs'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faXmark } from '@fortawesome/free-solid-svg-icons'

import { Button, Heading } from '~elements'
import { SlidingPanelProps } from 'types/calendar'
import styles from './SlidingPanel.module.scss'
import { ButtonProps } from '~types'
import { DATE_FORMATTER, NEXT_IMAGE_QUALITY, PST_TZ } from '~constants'
import { getGtmLinkPosition, isTicketmasterUrl, skipGtmPageview } from '~analyticsUtils'

const SlidingPanel = (props: SlidingPanelProps) => {
  // State variables
  const [disableBuyTicketsButton, setDisableBuyTicketsButton] = useState(true)
  const [activeButton, setActiveButton] = useState<string | string[] | null>(null)
  const [activeFormattedDate, setActiveFormattedDate] = useState<string | null>(null)
  const { artistDetail, disclaimer, eventInfo } = props ?? {}
  const [name, setName] = useState('')
  const [hostUrl, setHostUrl] = useState('')

  const { query } = useRouter()
  const router = useRouter()
  const linkModule = 'calendar_sliding_panel'
  const { id } = query

  // Find the selected artist based on the event info
  const selectedArtist = artistDetail.find(item => item?.artistId === eventInfo?.[0]?.artists?.[0]?.id)

  const eventTimestamp = eventInfo && dayjs.tz(eventInfo[0]['date_time'], PST_TZ)
  const formattedDate = eventTimestamp?.format(DATE_FORMATTER.FULL_DATE)
  const firstEventInfoValue = eventInfo && eventInfo[0]

  useEffect(() => {
    if (eventInfo && !id && props?.showSlidingWindow) {
      handleTimeButtonClick(firstEventInfoValue?.id, firstEventInfoValue?.name, firstEventInfoValue?.host_url)
    }
  }, [props?.showSlidingWindow])

  useEffect(() => {
    if (router?.isReady) {
      const { id } = router?.query
      if (id) {
        setActiveButton(id)
      }
    }
  }, [router?.isReady, router?.query?.id])

  useEffect(() => {
    if (props) {
      setName('')
    }
  }, [props])
  const handleShowSlidingWindow = () => {
    props.handleShowSlidingWindow(false)
    setDisableBuyTicketsButton(true)
    setActiveButton(null)

    document.getElementsByTagName('html')[0].style.overflowY = 'scroll'
  }
  useEffect(() => {
    if (!props?.showSlidingWindow) {
      setDisableBuyTicketsButton(true)
      setActiveButton(null)
      if (id) {
        router.push(
          {
            pathname: '/shows',
            query: ''
          },
          undefined,
          { shallow: true }
        )
      }
    } else {
      if (id && eventInfo) {
        const selectedId = eventInfo.filter(item => item?.id === id)
        handleTimeButtonClick(selectedId[0]?.id, selectedId[0]?.name, selectedId[0]?.host_url)
      }
    }
  }, [props?.showSlidingWindow])

  const handleEscapeKey = event => {
    if (event?.key === 'Escape') {
      handleShowSlidingWindow()
    }
  }

  useEffect(() => {
    document.addEventListener('keydown', handleEscapeKey)
    return () => {
      document.removeEventListener('keydown', handleEscapeKey)
    }
  }, [])

  const handleTimeButtonClick = (id: string, name: string, hostUrl: string) => {
    if (eventInfo) {
      const [showInfo] = eventInfo
      const { date_time: dateTime, host_url: defaultHostUrl } = showInfo
      const [filteredEventData] = eventInfo?.filter(show => show.id === id)
      const { host_url: tmUrl } = filteredEventData ?? {}
      const eventTimestamp = dateTime && dayjs.tz(dateTime, PST_TZ)
      const formattedDate = eventTimestamp?.format(DATE_FORMATTER.DATE)
      const updateHostUrl = hostUrl ? hostUrl : tmUrl ? tmUrl : defaultHostUrl
      const activeFormattedDate = dayjs(dateTime).format('MM/DD/YYYY')

      setActiveFormattedDate(activeFormattedDate)

      // Set flag to skip GTM pageview in handleRouteChangeComplete
      skipGtmPageview.current = true

      const queryParams = {
        startdate: formattedDate,
        id: id
      }

      router.push(
        {
          pathname: '/shows',
          query: queryParams
        },
        undefined,
        { shallow: true }
      )
      setActiveButton(id)
      setName(name)
      setHostUrl(updateHostUrl)
      setDisableBuyTicketsButton(false)
    }
  }

  return (
    <>
      <div className={styles['slide-module']} style={props.showSlidingWindow ? { right: 0 } : { right: '-512px' }}>
        <div className={styles['module-content-image']}>
          <FontAwesomeIcon icon={faXmark} style={{ fontSize: '10px' }} onClick={handleShowSlidingWindow} />
          {selectedArtist?.edpUrl && (
            <Link className={styles['module-content-link']} href={selectedArtist.edpUrl} target='_blank'>
              <div className={styles['module-content-image-content']}>
                {selectedArtist?.artistName && <Heading level={4}>{selectedArtist?.artistName}</Heading>}
              </div>
            </Link>
          )}
          {selectedArtist?.slideInPanelImage?.url && (
            <Image
              src={selectedArtist?.slideInPanelImage?.url}
              alt={selectedArtist?.slideInPanelImage?.altText || ''}
              style={{ objectFit: 'cover' }}
              fill
              quality={NEXT_IMAGE_QUALITY}
            />
          )}
        </div>
        <div className={styles['bottom-module']}>
          <div className={styles['module-content-calender']}>
            <div className={styles['list-item-date-holder']}>
              <div className={styles['list-item__date']}>
                <span className={styles['list-item__formatted-date']}>{formattedDate}</span>
              </div>
            </div>
            <div className={styles['list-item-ctas']}>
              {eventInfo &&
                eventInfo.map(data => {
                  return data ? (
                    <Button
                      key={data.id}
                      color='light'
                      fill={activeButton === data.id ? 'solid' : 'hollow'}
                      size='sm'
                      title={data.formatted_start_time}
                      onClick={() => handleTimeButtonClick(data?.id, data?.name, data?.host_url)}
                      analytics={{ skipEvent: true }}
                    />
                  ) : null
                })}
            </div>
          </div>
          <div className={styles['module-content-footer']}>
            {selectedArtist?.cta && selectedArtist?.cta.length > 0 && (
              <div className={styles['module-content-footer-buttons']}>
                {selectedArtist?.cta.map((button: ButtonProps, index: number) => {
                  const isTMLink = isTicketmasterUrl(button?.url)

                  return (
                    <Button
                      {...button}
                      key={`${button.title}_${index}`}
                      isTargetBlank={isTMLink}
                      disabled={disableBuyTicketsButton && isTMLink}
                      url={button?.url}
                      analytics={{
                        linkModule: linkModule,
                        linkPosition: getGtmLinkPosition(selectedArtist?.cta, index),
                        clickTracking: `${linkModule}:${selectedArtist?.artistName}:${button?.title}:cta`,
                        eventData: {
                          ticketmasterEventId: typeof activeButton === 'string' ? activeButton : '',
                          eventDate: activeFormattedDate
                        }
                      }}
                    />
                  )
                })}
              </div>
            )}
            {selectedArtist?.groupTicketOption && (
              <div className={styles['module-content-footer-text']}>
                {selectedArtist?.groupTicketOptionTextRedirectLink?.html &&
                  parse(selectedArtist?.groupTicketOptionTextRedirectLink?.html)}
              </div>
            )}
            {disclaimer?.html && (
              <div className={`${styles['module-content-footer-disclaimer']} `}>{parse(disclaimer.html)}</div>
            )}
          </div>
        </div>
      </div>
      {props.showSlidingWindow ? <div className={styles['overlay']} onClick={handleShowSlidingWindow}></div> : null}
    </>
  )
}

export default SlidingPanel
